import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import StepWelcome from './StepWelcome';
import StepName from './StepName';
import StepAgency from './StepAgency';
import StepMeet from './StepMeet';
import { getOnboardingCopy, ONBOARDING_TOTAL_STEPS } from './onboardingContent';
import { checkOnboardingPending, completeOnboarding } from './onboardingStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ONBOARDING V1 — Arrival → recognition → understanding → meet Arvexis.
// The step lives in the URL so refresh and browser Back behave sensibly.
const OnboardingV1 = ({ lang = 'en' }) => {
  const navigate = useNavigate();
  const COPY = getOnboardingCopy(lang);
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();

  const raw = parseInt(params.get('step') || '1', 10);
  const step = Number.isFinite(raw) ? Math.min(Math.max(raw, 1), ONBOARDING_TOTAL_STEPS) : 1;

  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [completeError, setCompleteError] = useState('');
  const [checked, setChecked] = useState(false);

  // Pre-fill with the existing account name; never assume the field is blank.
  useEffect(() => {
    if (user?.name) {
      setName((prev) => (prev ? prev : user.name));
      setSavedName(user.name);
    }
  }, [user?.name]);

  // A member who already finished onboarding must not be pulled back through it.
  useEffect(() => {
    let active = true;
    checkOnboardingPending().then((pending) => {
      if (!active) return;
      if (!pending) navigate('/home', { replace: true });
      else setChecked(true);
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const goTo = useCallback(
    (next, { replace = false } = {}) => {
      setParams({ step: String(next) }, { replace });
      window.scrollTo(0, 0);
    },
    [setParams]
  );

  const submitName = async () => {
    const trimmed = name.trim();
    setNameError('');

    // Nothing to persist: empty field, or unchanged from the stored account name.
    if (!trimmed || trimmed === savedName.trim()) {
      goTo(3);
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, { name: trimmed });
      setSavedName(trimmed);
      if (refreshUser) await refreshUser();
      goTo(3);
    } catch (e) {
      console.error('[Onboarding] name update failed:', e);
      setNameError(COPY.name.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const startConversation = async () => {
    setCompleteError('');
    setIsCompleting(true);
    try {
      await completeOnboarding();
      navigate('/companion', { replace: true });
    } catch (e) {
      // No fake success: the server marker stays as it is and we say so.
      console.error('[Onboarding] completion failed:', e);
      setCompleteError(COPY.meet.completeError);
    } finally {
      setIsCompleting(false);
    }
  };

  // Escape hatch offered only after a failed write, so a member is never stranded here.
  const skipCompletion = () => navigate('/companion', { replace: true });

  if (!checked) return <div className="min-h-[100dvh] w-full bg-[#05070f]" data-testid="onboarding-loading" />;

  const firstName = (savedName || name).trim().split(' ')[0] || '';

  if (step === 2) {
    return (
      <StepName
        copy={COPY}
        value={name}
        onChange={setName}
        onContinue={submitName}
        onBack={() => goTo(1)}
        isSaving={isSaving}
        error={nameError}
      />
    );
  }

  if (step === 3) return <StepAgency copy={COPY} onContinue={() => goTo(4)} onBack={() => goTo(2)} />;

  if (step === 4) {
    return (
      <StepMeet
        copy={COPY}
        firstName={firstName}
        onStart={startConversation}
        onSkipCompletion={skipCompletion}
        onBack={() => goTo(3)}
        isCompleting={isCompleting}
        error={completeError}
      />
    );
  }

  return <StepWelcome copy={COPY} onContinue={() => goTo(2)} />;
};

export default OnboardingV1;
